import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { runStartup } from '@/data/AppServices';
import { PRODUCT_NAME, type StartupState } from '@/domain/Models';
import { ArticleListPage, ArticlePlayerPage } from '@/features/article/ArticlePage';
import { HomePage } from '@/features/home/HomePage';
import { ScenarioPage } from '@/features/scenario/ScenarioPage';
import { SentenceListPage } from '@/features/sentence/SentenceListPage';
import { SentencePracticePage } from '@/features/sentence/SentencePracticePage';
import { CorpusManagementPage } from '@/features/settings/CorpusManagementPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { VocabularyDetailPage } from '@/features/vocabulary/VocabularyDetailPage';
import { VocabularyListPage } from '@/features/vocabulary/VocabularyListPage';
import { VocabularyPage } from '@/features/vocabulary/VocabularyPage';
import { isIosSafari } from '@/pwa/install';
import {
  IconArticle,
  IconScenario,
  IconSettings,
  IconToday,
  IconVocabulary,
} from '@/ui/icons';

function titleFor(path: string): string {
  if (path.startsWith('/today')) return '今日';
  if (path.startsWith('/vocabulary')) return '词汇';
  if (path.startsWith('/scenario/sentences')) return '例句练习';
  if (path.startsWith('/scenario')) return '对练';
  if (path.startsWith('/article')) return '听力';
  if (path.startsWith('/settings/corpus')) return '语料管理';
  if (path.startsWith('/settings')) return '设置';
  return PRODUCT_NAME;
}

function InstallGuide() {
  return (
    <div className="page stack" style={{ justifyContent: 'center', minHeight: '80vh' }}>
      <h1 style={{ margin: 0 }}>{PRODUCT_NAME}</h1>
      <p className="muted">先添加到主屏幕，再开始使用（语料约 70MB+，将在安装后下载）。</p>
      <ol className="stack" style={{ textAlign: 'left', gap: 8, padding: '0 8px' }}>
        <li>点底部中间的「分享」按钮</li>
        <li>选择「添加到主屏幕」</li>
        <li>返回主屏幕，点击「储能会员」图标打开</li>
      </ol>
      {isIosSafari() ? (
        <div className="muted">打开独立 App 后会自动下载语料并开始学习。</div>
      ) : (
        <div className="muted">请使用 iPhone 自带 Safari 浏览器访问本应用。</div>
      )}
    </div>
  );
}

export function App() {
  const [startup, setStartup] = useState<StartupState>({
    phase: 'idle',
    progress: 0,
    message: '',
    error: null,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const isPlayer = /^\/article\/.+/.test(location.pathname);
  const isTopLevel =
    location.pathname === '/today' ||
    location.pathname === '/vocabulary' ||
    location.pathname === '/scenario' ||
    location.pathname === '/article';
  const hideBottomNav = !isTopLevel;
  const hideTopbar = isPlayer;

  useEffect(() => {
    void runStartup(setStartup);
  }, []);

  if (startup.phase !== 'ready') {
    if (startup.phase === 'needs-install') {
      return (
        <div className="app-shell no-nav">
          <InstallGuide />
        </div>
      );
    }
    return (
      <div className="app-shell no-nav">
        <div className="page stack" style={{ justifyContent: 'center', minHeight: '80vh' }}>
          <h1 style={{ margin: 0 }}>{PRODUCT_NAME}</h1>
          <p className="muted">iOS 可安装网页应用 · 会员版 · 完全离线学习</p>
          {startup.phase === 'error' ? (
            <>
              <div className="error-box">{startup.error ?? '启动失败'}</div>
              <button type="button" onClick={() => void runStartup(setStartup)}>
                重试
              </button>
            </>
          ) : (
            <>
              <div className="muted">{startup.message || '准备中…'}</div>
              <div className="progress-bar">
                <span style={{ width: `${startup.progress}%` }} />
              </div>
              <div className="muted">首次启动需下载语料（约 70MB+），请保持页面打开。</div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`app-shell${hideBottomNav ? ' no-nav' : ''}`}>
      {!hideTopbar && (
        <header className="topbar">
          <h1>
            {!isTopLevel && (
              <button
                type="button"
                className="icon-btn"
                onClick={() => navigate(-1)}
                title="返回"
                aria-label="返回"
              >
                ‹
              </button>
            )}
            {titleFor(location.pathname)}
            {isTopLevel && <span className="member-badge">会员</span>}
          </h1>
          {isTopLevel && (
            <NavLink
              to="/settings"
              className="icon-btn"
              style={{ display: 'grid', placeItems: 'center', textDecoration: 'none' }}
              title="设置"
            >
              <IconSettings />
            </NavLink>
          )}
        </header>
      )}
      <Routes>
        <Route path="/today" element={<HomePage />} />
        <Route path="/vocabulary" element={<VocabularyListPage />} />
        <Route path="/vocabulary/practice" element={<VocabularyPage />} />
        <Route path="/vocabulary/:wordId" element={<VocabularyDetailPage />} />
        <Route path="/scenario" element={<ScenarioPage />} />
        <Route path="/scenario/practice/:scenarioId" element={<ScenarioPage />} />
        <Route path="/scenario/sentences" element={<SentenceListPage />} />
        <Route path="/scenario/sentences/practice" element={<SentencePracticePage />} />
        <Route path="/article" element={<ArticleListPage />} />
        <Route path="/article/:articleId" element={<ArticlePlayerPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/corpus" element={<CorpusManagementPage />} />
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Routes>
      <footer className="icp-footer">
        <a href="https://beian.miit.gov.cn" target="_blank" rel="noreferrer">
          沪ICP备2026026194号
        </a>
        <a href="https://beian.mps.gov.cn" target="_blank" rel="noreferrer">
          沪公网安备31011702891887号
        </a>
      </footer>
      {!hideBottomNav && (
        <nav className="bottom-nav">
          <NavLink to="/today" className={({ isActive }) => (isActive ? 'active' : undefined)}>
            <IconToday />
            <span>今日</span>
          </NavLink>
          <NavLink to="/vocabulary" className={({ isActive }) => (isActive ? 'active' : undefined)}>
            <IconVocabulary />
            <span>词汇</span>
          </NavLink>
          <NavLink to="/scenario" className={({ isActive }) => (isActive ? 'active' : undefined)}>
            <IconScenario />
            <span>对练</span>
          </NavLink>
          <NavLink to="/article" className={({ isActive }) => (isActive ? 'active' : undefined)}>
            <IconArticle />
            <span>听力</span>
          </NavLink>
        </nav>
      )}
    </div>
  );
}
